import { Outlet, useLocation } from 'react-router-dom';
import { PermissionsProvider } from '@/providers/permission-provider';
import { LanguageSelector, ProfileMenu } from '@/components/core';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Hot Wheels layout — full-width, no left sidebar.
 *
 * Top bar: brand link, nav (Dashboard / Collection / Stats / Wishlist),
 * language selector, profile menu. Notifications are intentionally not
 * wired up in this app — the bell, useGetNotifications fetch, and
 * <Notification /> panel have been removed.
 *
 * Body: <Outlet /> for the routed page.
 */

const NAV_ITEMS = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'Collection', to: '/collection' },
  { label: 'Stats', to: '/stats' },
  { label: 'Wishlist', to: '/wishlist' },
];

export const MainLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const segments = pathname?.split('/').filter(Boolean);
  const firstSegment = segments?.[0] ?? undefined;
  const isEmailRoute = firstSegment === 'mail';
  const isChatRoute = firstSegment === 'chat';
  const { data: user, isLoading: accountLoading } = useGetAccount();

  return (
    <PermissionsProvider user={user} isLoading={accountLoading}>
      <div className="flex flex-col w-full min-h-screen bg-zinc-950">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
          <div className="flex items-center justify-between gap-4 px-4 sm:px-6 md:px-10 h-16">
            <div className="flex items-center gap-8">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-xl font-bold text-white tracking-tight hover:text-blue-500 transition-colors"
              >
                <span className="text-[#2563EB]">Hot</span> Wheels
                <span className="text-zinc-500 ml-1 text-sm font-normal">Garage</span>
              </button>
              <nav className="hidden md:flex items-center gap-1">
                {NAV_ITEMS.map((item) => {
                  const isActive =
                    pathname === item.to ||
                    (item.to !== '/' && pathname.startsWith(item.to));
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-zinc-800 text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                      }`}
                    >
                      {t(item.label.toUpperCase().replace(/\s/g, '_'), item.label)}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-1 sm:gap-3">
              <LanguageSelector />
              <ProfileMenu />
            </div>
          </div>

          {/* Mobile nav */}
          <nav className="md:hidden flex items-center gap-1 px-4 pb-2 overflow-x-auto">
            {NAV_ITEMS.map((item) => {
              const isActive = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    isActive ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Body */}
        <div
          className={`flex-1 ${!isEmailRoute && !isChatRoute && 'p-4 sm:p-6 md:p-10'}`}
        >
          <Outlet />
        </div>
      </div>
    </PermissionsProvider>
  );
};